import React, { useCallback, useMemo, createContext, useContext, useState } from 'react';
import HoverMenu from 'material-ui-popup-state/HoverMenu';
import { usePopupState, bindHover, bindFocus, bindMenu } from 'material-ui-popup-state/hooks';

import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';

import { useTheme } from '@mui/material/styles';

import type {
  CascadingContextType,
  SelectMenuItemProps,
  CascadingMenuProps,
  CascadingSubmenuProps,
  SelectCascadingProps,
  CascadingOption,
  OptionValue,
} from 'components/mui/Form/Select/SelectCascading/types';

const CascadingContext = createContext<CascadingContextType>({
  parentPopupState: null,
  rootPopupState: null,
});

function SelectMenuItem({ handleClick, item, children, ...props }: SelectMenuItemProps) {
  // be careful
  // looks like Mui select reassign some properties for children - value, onClick, etc,
  // so we need to use item.value, handleClick
  const onClick = useCallback(
    (event: React.MouseEvent) => {
      // Prevent default menu item behavior
      event.preventDefault();
      event.stopPropagation();
      if (handleClick) {
        handleClick(event, item?.value);
      }
    },
    [handleClick, item],
  );

  return (
    <MenuItem {...props} onClick={onClick}>
      {children}
    </MenuItem>
  );
}

function CascadingMenu({ popupState, children, ...props }: CascadingMenuProps) {
  const { rootPopupState } = useContext(CascadingContext);
  const context = useMemo(
    () => ({
      rootPopupState: rootPopupState || popupState,
      parentPopupState: popupState,
    }),
    [rootPopupState, popupState],
  );

  return (
    <CascadingContext.Provider value={context}>
      <HoverMenu {...props} {...bindMenu(popupState)}>
        {children}
      </HoverMenu>
    </CascadingContext.Provider>
  );
}

function CascadingSubmenu({ title, popupId, children, ...props }: CascadingSubmenuProps) {
  const theme = useTheme();
  const { parentPopupState } = useContext(CascadingContext);
  const popupState = usePopupState({
    popupId,
    variant: 'popover',
    parentPopupState,
  });

  const isRTL = theme.direction === 'rtl';

  return (
    <>
      <MenuItem {...bindHover(popupState)} {...bindFocus(popupState)}>
        <Box component="span" sx={{ flexGrow: 1 }}>
          {title}
        </Box>
        {isRTL ? <ChevronLeftIcon sx={{ mr: -1 }} /> : <ChevronRightIcon sx={{ mr: -1 }} />}
      </MenuItem>
      <CascadingMenu
        {...props}
        slotProps={{
          paper: { sx: { marginTop: -1 } },
        }}
        anchorOrigin={{ vertical: 'top', horizontal: isRTL ? 'left' : 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: isRTL ? 'right' : 'left' }}
        popupState={popupState}
      >
        {children}
      </CascadingMenu>
    </>
  );
}

function SelectCascading({
  label,
  value,
  onChange,
  options = [],
  FormControlProps = {},
  InputLabelProps = {},
  id,
  ...props
}: SelectCascadingProps) {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  // Create a flat array of all options for quick lookup
  const flattenOptions = useCallback((optionsList: CascadingOption[]) => {
    const flattened: Array<{ value: any; label: React.ReactNode }> = [];

    const flatten = (items: CascadingOption[]) => {
      items.forEach((item) => {
        // Add the current item if it's selectable (no submenu or has value)
        if (!item.submenu || item.value) {
          flattened.push({
            value:
              typeof item.value === 'object' && item.value !== null && 'value' in item.value
                ? item.value.value
                : item.value,
            label: item.label,
          });
        }

        // Recursively flatten submenus
        if (item.submenu && item.submenu.length > 0) {
          flatten(item.submenu);
        }
      });
    };

    flatten(optionsList);

    return flattened;
  }, []);

  // Memoize the flattened options for performance
  const flatOptions = useMemo(() => flattenOptions(options), [options, flattenOptions]);

  // Simple lookup function using the flat array
  const findOptionLabel = useCallback(
    (searchValue: OptionValue): React.ReactNode | null => {
      // Extract the actual value if searchValue is an object
      const actualSearchValue =
        typeof searchValue === 'object' && searchValue !== null && 'value' in searchValue
          ? searchValue.value
          : searchValue;

      const foundOption = flatOptions.find((option) => option.value === actualSearchValue);
      return foundOption ? foundOption.label : null;
    },
    [flatOptions],
  );

  const handleItemClick = useCallback(
    (event: React.MouseEvent, selectedValue: any) => {
      setIsOpen(false);

      if (onChange) {
        onChange(event, selectedValue);
      }
    },
    [onChange],
  );

  const handleOpen = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Default render value function that works with nested options
  const renderValue = useCallback(
    (selectedValue: OptionValue): React.ReactNode => {
      if (selectedValue === '' || selectedValue == null) {
        return '';
      }

      // Handle object values - extract the actual value for comparison
      const actualValue =
        typeof selectedValue === 'object' && selectedValue !== null && 'value' in selectedValue
          ? selectedValue.value
          : selectedValue;

      const currentLabel = findOptionLabel(selectedValue);

      // Return the label as a string, or fall back to the actual value as string
      return currentLabel || String(actualValue);
    },
    [findOptionLabel],
  );

  const renderCascadingOptions = useCallback(
    (list: CascadingOption[]): React.ReactNode[] => {
      return list.map((item) => {
        const keyID =
          typeof item.value === 'object' && item.value !== null && 'value' in item.value
            ? item.value.value
            : item.value;

        if (item.submenu && item.submenu.length > 0) {
          return (
            <CascadingSubmenu key={keyID} title={item.label} popupId={`submenu-${keyID}`}>
              {renderCascadingOptions(item.submenu)}
            </CascadingSubmenu>
          );
        }

        return (
          <SelectMenuItem key={keyID} item={item} value={item.value} handleClick={handleItemClick}>
            {item.label}
          </SelectMenuItem>
        );
      });
    },
    [handleItemClick],
  );

  return (
    <FormControl {...FormControlProps}>
      <InputLabel id={`${id}-label`} {...InputLabelProps}>
        {label}
      </InputLabel>
      <Select
        labelId={`${id}-label`}
        label={label}
        value={value}
        open={isOpen}
        onOpen={handleOpen}
        onClose={handleClose}
        renderValue={renderValue}
        {...props}
      >
        {renderCascadingOptions(options)}
      </Select>
    </FormControl>
  );
}

export default SelectCascading;
